import React, {useContext, useRef} from 'react';

import Config from 'config';
import {Context} from 'context';
import * as TicketController from 'controller/TicketController';
import styles from 'editor/screens/Screen.module.scss';
import {COPY, copyFor} from 'text/errors';

/**
 * The full-page screen the project-open gate shows when the editor cannot open yet (R40, R47). It sits inside the themed `.app` wrapper that App
 * renders, so the theme classes colour it and the toasts still show.
 * @param {string} title
 * @param {string[]} body - one or two paragraphs
 * @param {{label: string, onClick: function}[]} [buttons] - the first is the main one
 * @param children - rendered between the paragraphs and the buttons
 */
export default function Screen({title, body, buttons = [], children}) {
    return (
        <div className={styles.page}>
            <div className={`${styles.card} bgSecondary txtPrimary`}>
                <h1>{title}</h1>
                {body.map((text, index) => <p key={index}>{text}</p>)}
                {children}
                {buttons.length > 0 && (
                    <div className={styles.buttons}>
                        {buttons.map(({label, onClick}, index) => (
                            <button key={label} type="button" className={`btn ${index === 0 ? 'btnBrand' : 'btnTertiary'}`} onClick={onClick}>{label}</button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/** "Back to projects": the dashboard, where every load error has always sent the page. */
export const backButton = {
    label: COPY.buttons.back,
    onClick: () => {
        window.location = Config.dashboard;
    },
};

/** "Retry": the provider runs the whole open flow again, from the project GET. */
export const useRetryButton = () => {
    const context = useContext(Context);
    return {label: COPY.buttons.retry, onClick: () => context.retryOpen()};
};

/**
 * "Report an issue": posts a ticket for the project and thanks the user. A ticket is posted once: clicks while one is on its way, and after one
 * went through, do nothing. When the post fails the user is told in the copy table's words and may click again.
 */
export const useReportButton = (projectId, code) => {
    const context = useContext(Context);
    const posted = useRef(false);
    return {
        label: COPY.buttons.report,
        onClick: async () => {
            if (posted.current) {
                return;
            }
            posted.current = true;
            try {
                await TicketController.createProjectReportTicket(projectId, code);
                context.alert(COPY.ticketSent, 'success');
            } catch (error) {
                posted.current = false;
                context.alert(copyFor(error), 'danger');
            }
        },
    };
};
